import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../App";
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway', pstbets_bw: 'PST Bets', sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267', bbets_bw: 'BBets', betxplosion_bw: 'BetXplosion',
};

const BOOKMAKER_URLS = {
  betway_bw: 'https://www.betway.co.bw',
  pstbets_bw: 'https://www.pstbet.co.bw',
  sunbet_bw: 'https://www.sunbet.co.bw',
  bet267_bw: 'https://www.bet267.co.bw',
  bbets_bw: 'https://www.bbets.co.bw',
  betxplosion_bw: 'https://www.betxplosion.co.bw',
};

const SPORT_EMOJI = { soccer: "⚽", basketball: "🏀", tennis: "🎾", americanfootball: "🏈", cricket: "🏏", rugby: "🏉", handball: "🤾", hockey: "🏒", baseball: "⚾" };
function sportEmoji(s = "") { return SPORT_EMOJI[Object.keys(SPORT_EMOJI).find(k => s.toLowerCase().includes(k))] || "🎯"; }

function formatKickoff(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleDateString("en-BW", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function timeAgo(dt) {
  if (!dt) return "—";
  const seconds = Math.floor((new Date() - new Date(dt)) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}

export default function Arbitrage() {
  const [arbs, setArbs] = useState([]);
  const [counts, setCounts] = useState({ prematch: 0, live: 0 });
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bankroll, setBankroll] = useState(100);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [tab, setTab] = useState('prematch'); // 'prematch' | 'live'
  const [sortBy, setSortBy] = useState('newest'); // 'newest' | 'profit'
  const [minProfit, setMinProfit] = useState(0);
  const [calcStakes, setCalcStakes] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

    const fetchArbs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100, isLive: tab === 'live' });
      if (minProfit) params.set("minProfit", minProfit);
      
      const [currentData, otherData] = await Promise.all([
        apiFetch(`/arbitrage?${params}`),
        apiFetch(`/arbitrage?limit=1&isLive=${tab !== 'live'}`),
      ]);
      
      if (currentData.arbs?.length > 0) {
        setArbs(currentData.arbs);
      } else if (!initialLoad) {
        // Keep existing arbs during scan gap
      } else {
        setArbs([]);
      }
      setCounts({
        prematch: tab === 'live' ? otherData.count : currentData.count,
        live: tab === 'live' ? currentData.count : otherData.count,
      });
      setLastRefresh(new Date());
    } catch (e) { console.error(e); }
        finally { setLoading(false); setInitialLoad(false); }
  }, [tab, minProfit]);

  useEffect(() => { fetchArbs(); }, [fetchArbs]);

  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden) fetchArbs();
    }, tab === 'live' ? 15000 : 30000);
    const handleVisibility = () => { if (!document.hidden) fetchArbs(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchArbs, tab]);

  const sortedArbs = [...arbs]
    .filter(a => a.profitPct >= minProfit)
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.detectedAt) - new Date(a.detectedAt);
      return b.profitPct - a.profitPct;
    });

  const calculateForBankroll = async (arb) => {
    setCalcLoading(true);
    try {
      const legs = arb.legs || [];
      const bookPct = legs.reduce((s, l) => s + (1 / l.price), 0);
      const calculatedLegs = legs.map(l => ({
        selection: l.outcome,
        bookmaker: l.bookmaker,
        odds: l.price,
        stake: Number(((bankroll * (1 / l.price)) / bookPct).toFixed(2)),
        payout: Number(((bankroll * (1 / l.price)) / bookPct * l.price).toFixed(2)),
      }));
      const guaranteedReturn = Number((bankroll / bookPct).toFixed(2));
      setCalcStakes({
        legs: calculatedLegs,
        totalStake: bankroll,
        guaranteedReturn,
        profitBWP: Number((guaranteedReturn - bankroll).toFixed(2)),
      });
    } catch (e) { console.error(e); }
    finally { setCalcLoading(false); }
  };

  const handleSelect = (arb) => {
    setSelected(s => s?._id === arb._id ? null : arb);
    setCalcStakes(null);
  };

  const liveCount = counts.live;
  const prematchCount = counts.prematch;
  const bestArb = sortedArbs[0];

  return (
    <div className="no-select" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Watermark />

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 10 }}>
        {[
          { label: "Prematch Arbs", value: loading ? "—" : prematchCount, color: "var(--accent)" },
          { label: "Live Arbs", value: loading ? "—" : liveCount, color: "var(--success)" },
          { label: "Best Profit", value: bestArb ? `+${bestArb.profitPct?.toFixed(2)}%` : "—", color: "var(--accent2)" },
          { label: "Best at P500", value: bestArb ? `P${((bestArb.profitPct / 100) * 500).toFixed(0)}` : "—", color: "var(--accent3)" },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ "--accent-color": s.color, textAlign: "center" }}>
            <div className="stat-label" style={{ textAlign: "center", fontSize: 10 }}>{s.label}</div>
            <div className="stat-value" style={{ textAlign: "center", fontSize: isMobile ? 18 : 24 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Explainer */}
      <div style={{
        background: "rgba(61,142,248,0.06)", border: "1px solid rgba(61,142,248,0.15)",
        borderRadius: "var(--radius)", padding: "14px 18px",
        display: "flex", gap: 14, alignItems: "flex-start",
      }}>
        <div style={{ fontSize: 22, flexShrink: 0 }}>💡</div>
        <div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 13, marginBottom: 4 }}>How arbitrage works</div>
          <div style={{ fontSize: 12, color: "var(--text3)", lineHeight: 1.6 }}>
            When bookmakers price the same match differently, the combined implied probability can drop below 100% — creating a guaranteed profit. Place the exact stake on each leg simultaneously to lock in the return. ⚠️ Arb windows close fast — act quickly.
          </div>
        </div>
      </div>

      {/* Tabs + filters */}
      <div className="card" style={{ padding: "0 18px" }}>
        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
          {[
            { key: 'prematch', label: '📅 Pre-match' },
            { key: 'live', label: '🔴 Live' },
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setSelected(null); setCalcStakes(null); setArbs([]); setInitialLoad(true); }} style={{
              padding: "12px 18px", fontFamily: "var(--font-display)", fontWeight: 600,
              fontSize: 12, textTransform: "uppercase", letterSpacing: .5,
              color: tab === t.key ? "var(--accent)" : "var(--text3)",
              background: "transparent", border: "none",
              borderBottom: tab === t.key ? "2px solid var(--accent)" : "2px solid transparent",
              cursor: "pointer",
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "12px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5 }}>Min Profit</label>
            <select value={minProfit} onChange={e => setMinProfit(parseFloat(e.target.value))} style={{ fontSize: 13 }}>
              <option value={0}>Any</option>
              <option value={0.5}>+0.5%</option>
              <option value={1}>+1%</option>
              <option value={2}>+2%</option>
              <option value={3}>+3%</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5 }}>Sort</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ fontSize: 13 }}>
              <option value="newest">Newest first</option>
              <option value="profit">Highest profit</option>
            </select>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            {lastRefresh && <span style={{ fontSize: 11, color: "var(--text3)" }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button className="btn btn-outline" style={{ fontSize: 11, padding: "5px 12px" }} onClick={fetchArbs} disabled={loading}>
              ↺ Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Arbs list + detail */}
      <div style={{ display: "grid", gridTemplateColumns: selected && !isMobile ? "1fr 380px" : "1fr", gap: 16, alignItems: "start" }}>
        <div className="card">
            {loading && initialLoad ? (
            <div style={{ padding: 20 }}>
              {[...Array(5)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80, marginBottom: 10 }} />)}
            </div>
          ) : sortedArbs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">⚡</div>
              <p>No {tab === 'live' ? 'live' : 'pre-match'} arbitrage opportunities right now</p>
              <p style={{ fontSize: 12, marginTop: 6 }}>Check back soon — arbs appear and disappear quickly</p>
            </div>
          ) : sortedArbs.map(arb => (
            <div
              key={arb._id}
              onClick={() => handleSelect(arb)}
              style={{
                padding: isMobile ? "12px 14px" : "16px 20px",
                borderBottom: "1px solid var(--border)",
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr auto" : "36px 1fr auto",
                gap: isMobile ? 10 : 14,
                alignItems: "center",
                cursor: "pointer",
                background: selected?._id === arb._id ? "var(--bg4)" : "transparent",
                transition: "background .12s",
              }}
            >
              {!isMobile && (
                <div className="sport-badge" style={{ background: "rgba(0,229,176,0.07)", fontSize: 16 }}>{sportEmoji(arb.sport)}</div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {isMobile ? sportEmoji(arb.sport) + " " : ""}{arb.homeTeam} vs {arb.awayTeam}
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                  {arb.market} · {arb.league || arb.sport} · {formatKickoff(arb.startTime || arb.commenceTime)}
                </div>
                <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>
                  Detected {timeAgo(arb.detectedAt)}
                </div>
                <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {arb.legs?.map((leg, i) => (
                    <span key={i} className="pill pill-blue" style={{ fontSize: 10 }}>
                      {leg.outcome} @ {leg.price} — {BOOKMAKER_LABELS[leg.bookmaker] || leg.bookmaker}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: isMobile ? 18 : 22, color: "var(--success)" }}>
                  +{arb.profitPct?.toFixed(2)}%
                </div>
                <div style={{ fontSize: 11, color: "var(--text3)" }}>guaranteed</div>
                <div style={{ fontSize: 12, color: "var(--accent)", marginTop: 4, fontWeight: 600 }}>
                  P{((arb.profitPct / 100) * 500).toFixed(0)} / P500
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detail + calculator */}
        {selected && (
          isMobile ? (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(8,13,23,0.97)',
              backdropFilter: 'blur(8px)',
              overflow: 'auto', padding: '20px 16px',
            }}>
              <div className="card" style={{ maxWidth: 480, margin: '0 auto' }}>
                <div className="section-header">
                  <div className="section-title">⚡ Arb Calculator</div>
                  <button onClick={() => { setSelected(null); setCalcStakes(null); }} style={{ color: "var(--text3)", fontSize: 22, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                </div>
                <ArbDetail selected={selected} bankroll={bankroll} setBankroll={setBankroll} calcStakes={calcStakes} calcLoading={calcLoading} onCalculate={() => calculateForBankroll(selected)} />
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, position: "sticky", top: 80 }}>
              <div className="card">
                <div className="section-header">
                  <div className="section-title">⚡ Arb Calculator</div>
                  <button onClick={() => { setSelected(null); setCalcStakes(null); }} style={{ color: "var(--text3)", fontSize: 18, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                </div>
                <ArbDetail selected={selected} bankroll={bankroll} setBankroll={setBankroll} calcStakes={calcStakes} calcLoading={calcLoading} onCalculate={() => calculateForBankroll(selected)} />
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function ArbDetail({ selected, bankroll, setBankroll, calcStakes, calcLoading, onCalculate }) {
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>
          {selected.homeTeam} vs {selected.awayTeam}
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 3 }}>
          {selected.sport} · {selected.league} · {formatKickoff(selected.startTime || selected.commenceTime)}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
          Detected {timeAgo(selected.detectedAt)}
        </div>
      </div>

      <div style={{
        background: "rgba(18,201,142,0.06)", border: "1px solid rgba(18,201,142,0.2)",
        borderRadius: "var(--radius-sm)", padding: 14, textAlign: "center"
      }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 32, color: "var(--success)" }}>
          +{selected.profitPct?.toFixed(2)}%
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)" }}>guaranteed profit margin</div>
      </div>

      {/* Quick links */}
      <div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 8, fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5 }}>
          Open bookmakers
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[...new Set(selected.legs?.map(l => l.bookmaker))].map(bm => (
            <a key={bm} href={BOOKMAKER_URLS[bm] || '#'} target="_blank" rel="noopener noreferrer"
              className="btn btn-outline" style={{ fontSize: 11, padding: "5px 12px" }}>
              {BOOKMAKER_LABELS[bm] || bm} ↗
            </a>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6, fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5 }}>
          Total stake (BWP)
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="number" value={bankroll}
            onChange={e => setBankroll(parseFloat(e.target.value) || 100)}
            style={{ flex: 1, fontSize: 16, fontFamily: "var(--font-display)", fontWeight: 700 }}
            placeholder="100"
          />
          <button className="btn btn-primary" onClick={onCalculate} disabled={calcLoading}>
            {calcLoading ? "..." : "Calculate"}
          </button>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {[100, 200, 500, 1000].map(a => (
            <button key={a} className="btn btn-ghost" style={{ fontSize: 11, padding: "4px 10px" }} onClick={() => setBankroll(a)}>
              P{a}
            </button>
          ))}
        </div>
      </div>

      {calcStakes && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5 }}>Place these bets:</div>
          {calcStakes.legs?.map((leg, i) => (
            <div key={i} style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: "12px 14px", display: "grid", gridTemplateColumns: "1fr auto" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{leg.selection}</div>
                <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>
                  {BOOKMAKER_LABELS[leg.bookmaker] || leg.bookmaker} · @ {leg.odds}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--accent)", fontSize: 16 }}>P{leg.stake}</div>
                <div style={{ fontSize: 10, color: "var(--text3)" }}>→ P{leg.payout}</div>
              </div>
            </div>
          ))}
          <div style={{
            background: "rgba(18,201,142,0.08)", border: "1px solid rgba(18,201,142,0.2)",
            borderRadius: "var(--radius-sm)", padding: "12px 14px",
            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 11, color: "var(--text3)" }}>Total staked</div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16 }}>P{calcStakes.totalStake}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, color: "var(--text3)" }}>Guaranteed return</div>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--success)" }}>P{calcStakes.guaranteedReturn}</div>
            </div>
            <div style={{ gridColumn: "1/-1", borderTop: "1px solid rgba(18,201,142,0.2)", paddingTop: 10, textAlign: "center" }}>
              <span style={{ fontSize: 12, color: "var(--text3)" }}>Guaranteed profit: </span>
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: "var(--success)" }}>+P{calcStakes.profitBWP}</span>
            </div>
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", textAlign: "center", lineHeight: 1.5 }}>
            ⚠️ Place all bets simultaneously — arb windows close fast. Verify odds before placing.
          </div>
        </div>
      )}
    </div>
  );
}